import { useCallback } from "react";
import client from "../helpers/network/client";
import { sortByKey } from "../helpers/utils/array.util";
import { School, SchoolDto } from "../models/school/school";
import { Teacher, TeacherDto } from "../models/teacher/teacher";
import useCache from "../hooks/useCache";
import { Address } from "../models/address";
import { SortAlphabeticallyWithKey } from "../helpers/helper-functions";

const getSchool = (id: string) =>
  client.get<SchoolDto>(`school/${id}`).then((r) => r.data);

const getSchools = () => client.get<SchoolDto[]>(`school`).then((r) => r.data);

const getTeachersOfSchool = (id: string) =>
  client
    .get<TeacherDto[]>(`school/${id}/teacher`)
    .then((r) => sortByKey(r.data, "name"));

const addExternalTeacherToSchool = (
  schoolId: string,
  teacherEmail: string,
  details: { lastName?: string } & Omit<Address, "lastname">,
) =>
  client
    .put(`school/${schoolId}/teacher/${teacherEmail}/external`, details)
    .then((r) => r.data);

export const useSchoolApi = () => {
  const { fetchWithCache } = useCache();

  const _getTeachersOfSchool = useCallback(
    async (schoolId: string) => {
      const teachers = await fetchWithCache(
        { type: "teacher", contexts: [schoolId] },
        () => getTeachersOfSchool(schoolId),
      );
      return teachers.map(Teacher.fromTeacherDto).sort(SortAlphabeticallyWithKey('fullName'));
    },
    [fetchWithCache],
  );

  const _getSchool = useCallback(
    async (id: string) => {
      const school = await fetchWithCache({ type: "school", key: id }, () =>
        getSchool(id),
      );
      const teachers = await _getTeachersOfSchool(id);

      return School.createFromSchoolDto(school, teachers);
    },
    [_getTeachersOfSchool, fetchWithCache],
  );

  const _getSchools = useCallback(async () => {
    const schools = await fetchWithCache({ type: "school" }, () =>
      getSchools(),
    );
    return Promise.all(
      schools.map(async (school) => {
        const teachers = await _getTeachersOfSchool(school.id);
        return School.createFromSchoolDto(school, teachers);
      }),
    );
  }, [_getTeachersOfSchool, fetchWithCache]);

  return {
    getTeachersOfSchool: _getTeachersOfSchool,
    getSchool: _getSchool,
    getSchools: _getSchools,
    addExternalTeacherToSchool,
  };
};
